import type { BandName, MeterConfig, PersonaProfile, ScenarioDoc } from '../engine/types';
import type { ObjectionRead, PersonaRead, SimTurn } from '../engine/useSimulation';
import type { Outcome } from '@core/simulation/outcomes';
import { SentimentMeter } from './SentimentMeter';
import { signalCopy } from '../engine/buyingSignals';

/**
 * §10.5 — how each ending is rendered. `transfer_dropped` is stated plainly on
 * purpose: the full lead cost was spent and the closer never spoke to anyone.
 */
const OUTCOME_COPY: Record<Outcome, { title: string; body: string }> = {
  transfer_clean: {
    title: 'Clean transfer',
    body: 'The prospect stayed engaged through the handoff. Priya picked up to someone who wanted to be there.',
  },
  transfer_soft: {
    title: 'They held, but went quiet',
    body: 'The transfer completed and Priya picked up to someone noticeably cooler than the person you were speaking to.',
  },
  transfer_dropped: {
    title: 'They agreed to hold — and hung up',
    body: 'The full lead cost was spent and the closer never spoke to anyone. This is the most expensive way a call can end.',
  },
  closed_clean: { title: 'Closed', body: 'They bought, and they meant it.' },
  closed_soft: { title: 'Closed, then cancelled', body: 'They bought, and cancelled inside the refund window.' },
  closed_cancelled: {
    title: 'Cancelled',
    body: 'They agreed while they were not ready, and it came back. This is what a yes earned too easily looks like a fortnight later.',
  },
  qualified_out: {
    title: 'You qualified them out',
    body: 'You told someone who should not buy that they should not buy, and you were right. This passes, and it is the strongest thing on your record.',
  },
  no_show: { title: 'No show', body: 'The appointment was set and nobody came.' },
  lost: { title: 'They declined', body: 'The call ended without a transfer.' },
};

const TIER_LABEL: Record<string, string> = {
  best: 'Best available', good: 'Workable', weak: 'Cost you', bad: 'Damaging',
};

/**
 * The end of a practice call. Nothing here is recorded: a practice run is
 * unlimited and fully revealing, and it is not scored for certification (§3).
 * Close outcomes resolved against the band, and the scored record, arrive with
 * the certification path.
 */
export function SimulationSummary({
  scenario, turns, band, value, meter, persona, revealing, onDone, onAgain,
  outcome, outcomePassed, closeBand, personaRead, objectionReads, personas,
}: {
  scenario: ScenarioDoc;
  turns: SimTurn[];
  band: BandName;
  value: number;
  meter: MeterConfig;
  persona: PersonaProfile | null;
  revealing: boolean;
  outcome: Outcome | null;
  outcomePassed: boolean | null;
  closeBand: BandName;
  personaRead: PersonaRead | null;
  objectionReads: ObjectionRead[];
  personas: PersonaProfile[];
  onDone: () => void;
  onAgain: () => void;
}) {
  const missteps = turns.filter((t) => t.tier === 'weak' || t.tier === 'bad');
  const violations = turns.filter((t) => t.compliance);
  // §6.10 — every question they were asked, whether or not they mishandled it.
  // Getting the read right is worth being told about: the whole difficulty is
  // that both kinds feel like progress, so a rep who called one correctly has
  // no way of knowing they did unless somebody says so.
  const questions = turns.filter((t) => signalCopy(t.buyingSignal));

  return (
    <main className="summary">
      <div className={`verdict ${outcomePassed === true ? 'pass' : outcomePassed === false ? 'fail' : ''}`}>
        <h1>{outcome ? OUTCOME_COPY[outcome].title : 'Call ended'}</h1>
        {outcome && <p className="outcome-body">{OUTCOME_COPY[outcome].body}</p>}
        <p className="muted small">
          {persona ? `${persona.name} · ` : ''}{scenario.title}
          {outcome !== 'qualified_out' && ` · ${closeBand} at the transfer`}
        </p>
        <div className="summary-meter">
          <SentimentMeter value={value} band={band} meter={meter} />
        </div>
        <Trajectory turns={turns} />
      </div>

      {personaRead && (
        <section className="review">
          <h2>How you read them</h2>
          <ul className="review-list">
            <li className={`review-item ${personaRead.correct ? '' : `sev-${personaRead.severity}`}`}>
              <span className="review-label">
                {personaRead.correct
                  ? <>You called it — <strong>{persona?.name}</strong></>
                  : <>You read <strong>{label(personaRead.selected, personas)}</strong>, and they were <strong>{persona?.name}</strong></>}
                {personaRead.revisedFrom && (
                  <span className="small muted"> · you revised your first read</span>
                )}
              </span>
              {!personaRead.correct && personaRead.severity && (
                <span className={`pill pill-${personaRead.severity}`}>{personaRead.severity}</span>
              )}
            </li>
            {objectionReads.map((r) => (
              <li key={r.nodeId} className={`review-item ${r.correct ? '' : 'sev-moderate'}`}>
                <span className="review-label">
                  {r.correct
                    ? <>You named the objection correctly — <strong>{r.actual.replace(/_/g, ' ')}</strong></>
                    : <>You handled <strong>{r.selected.replace(/_/g, ' ')}</strong>; they raised <strong>{r.actual.replace(/_/g, ' ')}</strong></>}
                </span>
              </li>
            ))}
          </ul>
          {!personaRead.correct && (
            <p className="small muted">
              From the moment you named them, you were offered the right moves for
              somebody else. That is what the meter was reacting to.
            </p>
          )}
        </section>
      )}

      {violations.length > 0 && (
        <section className="review">
          <h2>Lines you should not have crossed</h2>
          <ul className="review-list">
            {violations.map((t, i) => (
              <li key={i} className="review-item sev-severe">
                <span className="review-label">“{t.optionText}”</span>
                <span className="pill pill-severe">{t.compliance!.replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {revealing && questions.length > 0 && (
        <section className="review">
          <h2>What they were asking you</h2>
          <ul className="review-list">
            {questions.map((t, i) => (
              <li key={i} className="review-item">
                <span className="review-label">
                  <strong>“{t.line}”</strong>
                  <span className="small muted"> — that was {signalCopy(t.buyingSignal)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {revealing && (
        <section className="review">
          <h2>{missteps.length > 0 ? 'Where the call turned' : 'Every turn'}</h2>
          <ul className="review-list">
            {(missteps.length > 0 ? missteps : turns).map((t, i) => (
              <li key={i} className={`review-item tier-${t.tier ?? 'none'}`}>
                <span className="review-label">
                  <strong>“{t.optionText}”</strong>
                  <span className="small muted"> — {t.explain.what}</span>
                </span>
                <span className={`pill pill-tier-${t.tier ?? 'bad'}`}>{TIER_LABEL[t.tier ?? 'bad']}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <button className="primary wide" onClick={onAgain}>Run it again</button>
      <button className="ghost wide" onClick={onDone}>Back to your progress</button>
    </main>
  );
}

/** Names come from the content pack, never from the engine (§1.1). */
function label(id: string, personas: PersonaProfile[]): string {
  return personas.find((p) => p.id === id)?.name
    ?? id.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** The shape of the call, without ever showing a number (§10.4). */
function Trajectory({ turns }: { turns: SimTurn[] }) {
  if (turns.length < 2) return null;
  const points = [turns[0]!.from, ...turns.map((t) => t.to)];
  const w = 260, h = 56, pad = 4;
  const step = (w - pad * 2) / (points.length - 1);
  const y = (v: number) => h - pad - (v / 100) * (h - pad * 2);
  const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * step} ${y(v)}`).join(' ');

  return (
    <svg className="trajectory" viewBox={`0 0 ${w} ${h}`} role="img"
         aria-label="How the prospect's mood moved through the call">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
      {points.map((v, i) => (
        <circle key={i} cx={pad + i * step} cy={y(v)} r={i === points.length - 1 ? 3.5 : 2} />
      ))}
    </svg>
  );
}
